import { BlockStack, Card, Select, Text, TextField } from "@shopify/polaris"
import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ColorPick } from "@/component"

import { useBackdropThemeLayout } from "./BackdropThemeLayoutContext"
import { normalizeBackdropDesktopHexToSix } from "./themeLayoutBackdropEditor"

/** Backdrop「样式」Tab：Order lookup + Display（与右侧预览联动） */
const ThemeLayoutLeftStyleBackdropTabPanel = () => {
  const { t } = useTranslation("tracking_page")
  const { draft, setDraft } = useBackdropThemeLayout()
  const style = draft.style

  const sizeOptions = useMemo(
    () => [
      { label: t("themeLayoutBackdropStyleSizeSmall"), value: "SMALL" },
      { label: t("themeLayoutBackdropStyleSizeStandard"), value: "STANDARD" },
      { label: t("themeLayoutBackdropStyleSizeLarge"), value: "LARGE" },
    ],
    [t],
  )

  const positionOptions = useMemo(
    () => [
      { label: t("themeLayoutBackdropStylePositionLeft"), value: "LEFT" },
      { label: t("themeLayoutBackdropStylePositionCenter"), value: "CENTER" },
      { label: t("themeLayoutBackdropStylePositionRight"), value: "RIGHT" },
    ],
    [t],
  )

  const cornerRadiusOptions = useMemo(
    () => [
      { label: t("themeLayoutBackdropCornerRadiusSharp"), value: "SHARP" },
      { label: t("themeLayoutBackdropCornerRadiusStandard"), value: "STANDARD" },
      { label: t("themeLayoutBackdropCornerRadiusRounded"), value: "ROUNDED" },
    ],
    [t],
  )

  const handleSizeChange = useCallback(
    (value: string) => {
      if (value !== "SMALL" && value !== "STANDARD" && value !== "LARGE") {
        return
      }
      setDraft((prev) => ({ ...prev, style: { ...prev.style, size: value } }))
    },
    [setDraft],
  )

  const handlePositionChange = useCallback(
    (value: string) => {
      if (value !== "LEFT" && value !== "CENTER" && value !== "RIGHT") {
        return
      }
      setDraft((prev) => ({ ...prev, style: { ...prev.style, position: value } }))
    },
    [setDraft],
  )

  const handleCornerRadiusChange = useCallback(
    (value: string) => {
      if (value !== "SHARP" && value !== "STANDARD" && value !== "ROUNDED") {
        return
      }
      setDraft((prev) => ({ ...prev, style: { ...prev.style, corner_radius: value } }))
    },
    [setDraft],
  )

  const handleDesktopHexChange = useCallback(
    (value: string) => {
      const raw = value.replace(/#/g, "").toUpperCase()
        .replace(/[^0-9A-F]/g, "")
        .slice(0, 6)

      setDraft((prev) => ({
        ...prev,
        style: { ...prev.style, desktop_bg_color: raw },
      }))
    },
    [setDraft],
  )

  const colorPickValue = useMemo(() => {
    const normalized = normalizeBackdropDesktopHexToSix(style.desktop_bg_color)

    if (normalized) {
      return normalized
    }

    return "FFFFFF"
  }, [style.desktop_bg_color])

  return (
    <BlockStack gap="400">
      <Card>
        <Text variant="headingMd" as="h3">
          {t("OrderLookupWidget")}
        </Text>
        <div className="mt-1">
          <Text as="p" tone="subdued">
            {t("OrderLookupWidgetDescription")}
          </Text>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <Select
            label={t("themeLayoutBackdropStyleSize")}
            options={sizeOptions}
            value={style.size}
            onChange={handleSizeChange}
          />
          <Select
            label={t("themeLayoutBackdropStylePosition")}
            options={positionOptions}
            value={style.position}
            onChange={handlePositionChange}
          />

          <div className="flex max-w-full flex-col gap-1">
            <Text as="p" variant="bodyMd">
              {t("themeLayoutBackdropDesktopBackgroundColor")}
            </Text>
            <div className="flex max-w-full flex-wrap items-end gap-2">
              <div className="relative z-[1] shrink-0 [&_.Polaris-LegacyStack]:items-end">
                <ColorPick
                  onlyColor
                  activatorStyle={{ width: 32, height: 32 }}
                  color={colorPickValue}
                  onChange={(v) => {
                    handleDesktopHexChange(v)
                  }}
                  placeholder="FFFFFF"
                />
              </div>
              <div className="min-w-0 flex-1 basis-[180px]">
                <TextField
                  autoComplete="off"
                  label={null}
                  prefix="#"
                  value={style.desktop_bg_color}
                  onChange={handleDesktopHexChange}
                  placeholder="FFFFFF"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <Text variant="headingMd" as="h3">
          {t("Display")}
        </Text>
        <div className="mt-4 flex flex-col gap-3">
          <Select
            label={t("themeLayoutBackdropDisplayCornerRadius")}
            options={cornerRadiusOptions}
            value={style.corner_radius}
            onChange={handleCornerRadiusChange}
          />
        </div>
      </Card>
    </BlockStack>
  )
}

export default ThemeLayoutLeftStyleBackdropTabPanel
